import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Clock, Award } from "lucide-react";
import api from "../lib/api";
import { EditableText } from "./live/Editable";

const TABS = [
  { key: "long_term", label: "Long Term", to: "/courses/long-term" },
  { key: "earn_while_learn", label: "Earn While You Learn", to: "/courses/earn-while-you-learn" },
  { key: "short_term", label: "Short Term", to: "/courses/short-term" },
  { key: "corporate", label: "Corporate", to: "/courses/corporate" },
];

export default function CoursesSection() {
  const [courses, setCourses] = useState([]);
  const [active, setActive] = useState("long_term");

  useEffect(() => {
    api
      .get("/courses", { params: { active_only: true } })
      .then((r) => setCourses(r.data))
      .catch(() => {});
  }, []);

  // Earn While You Learn = the long-term work-study programmes
  const activeCourses =
    active === "earn_while_learn"
      ? courses.filter((c) => c.category === "long_term")
      : courses.filter((c) => c.category === active);

  const activeTab = TABS.find((t) => t.key === active);

  return (
    <section className="relative py-24 bg-slate-50 overflow-hidden" data-testid="home-courses">
      <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-100/40 rounded-full blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <EditableText path="home.courses.kicker" as="p" className="kicker mb-3">
            German-certified programmes
          </EditableText>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B2A6B] leading-tight">
            <EditableText path="home.courses.heading" as="span">
              Explore our courses
            </EditableText>
          </h2>
        </div>

        {/* Tabs — single scrollable row */}
        <div className="flex flex-nowrap sm:flex-wrap sm:justify-center gap-2 mb-10 overflow-x-auto pb-2 -mx-1 px-1 no-scrollbar">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              data-testid={`course-tab-${t.key}`}
              className={`shrink-0 px-5 py-2.5 text-sm font-semibold rounded-full transition border whitespace-nowrap ${
                active === t.key
                  ? "bg-[#0B2A6B] text-white border-[#0B2A6B] shadow-md"
                  : "bg-white text-slate-700 border-slate-200 hover:border-[#0B2A6B] hover:text-[#0B2A6B]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Course grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {activeCourses.map((c, i) => {
            const Icon = Icons[c.icon] || Icons.GraduationCap;
            return (
              <Link
                key={c.id}
                to={`/courses/detail/${c.slug}`}
                data-testid={`home-course-${c.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-slate-100 tilt-card animate-fade-up flex flex-col"
                style={{ animationDelay: `${i * 0.06}s` }}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={c.image_url}
                    alt={c.title}
                    className="absolute inset-0 w-full h-full object-cover zoom-img"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className="px-3 py-1 rounded-full bg-yellow-400 text-[#0B2A6B] text-[11px] font-bold uppercase tracking-wider">
                      {c.category.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center text-white text-xs font-medium">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {c.duration}
                    </span>
                    {c.featured && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-white/20 rounded-full backdrop-blur">
                        <Award className="h-3 w-3" />
                        Featured
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="h-10 w-10 shrink-0 rounded-lg bg-yellow-400/15 group-hover:bg-yellow-400 transition flex items-center justify-center">
                      <Icon className="h-5 w-5 text-yellow-600 group-hover:text-[#0B2A6B] transition" />
                    </div>
                    <h3 className="font-bold text-[#0B2A6B] text-lg leading-snug group-hover:text-yellow-600 transition">
                      {c.title}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-3 leading-relaxed">
                    {c.description}
                  </p>
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-xs text-slate-500 line-clamp-1">{c.eligibility}</span>
                    <span className="text-yellow-600 font-bold text-sm inline-flex items-center gap-1 group-hover:translate-x-1 transition shrink-0">
                      Learn more <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {activeCourses.length === 0 && (
          <div className="text-center py-16 text-slate-500">No courses available.</div>
        )}

        {/* View all in this category */}
        <div className="mt-12 text-center">
          <Link
            to={activeTab?.to || "/courses"}
            data-testid="course-viewall"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-[#0B2A6B] text-white font-semibold hover:bg-[#061a47] transition group"
          >
            View all {activeTab?.label} courses
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition" />
          </Link>
        </div>
      </div>
    </section>
  );
}
